/**
 * AppointmentDetailPage - View appointment details and complete with visit notes
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Calendar,
  Clock,
  User,
  Stethoscope,
  ClipboardList,
  Thermometer,
  MessageSquareText,
  Pill,
  AlertTriangle,
  Activity,
  Edit3,
  CheckCircle2,
  Printer,
  Share2,
  Loader2,
  FileText
} from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';
import API from '@/utils/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const THEME = {
  bgGradient: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 30%, #F0FDFA 60%, #F5F3FF 100%)',
};

const AppointmentDetailPage = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const { isDemoMode } = useDemo();
  
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointment();
  }, [appointmentId]);

  const fetchAppointment = async () => {
    if (isDemoMode) {
      setLoading(false);
      return;
    }

    try {
      const response = await API.get(`/medical/appointment-preps/${appointmentId}/full`);
      setAppointment(response.data);
    } catch (error) {
      console.error('Failed to fetch appointment:', error);
      toast.error('Failed to load appointment');
      navigate('/medical');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Appointment - ${appointment?.doctorName || 'Doctor Visit'}`,
          text: `Appointment on ${appointment?.appointmentDate ? format(parseISO(appointment.appointmentDate), 'MMMM d, yyyy') : ''}`,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      toast.info('Sharing not supported on this device');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: THEME.bgGradient }}>
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: THEME.bgGradient }}>
        <div className="text-center">
          <p className="text-slate-600 mb-4">Appointment not found</p>
          <Button onClick={() => navigate('/medical')}>Back to Medical</Button>
        </div>
      </div>
    );
  }

  const isCompleted = appointment.status === 'completed';

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="appointment-detail-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-emerald-100 print:hidden">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/medical')}
              className="p-2 -ml-2"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">
                {isCompleted ? 'Visit Record' : 'Appointment Details'}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleShare}>
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        <Card className={`${isCompleted ? 'border-green-200 bg-green-50/50' : 'border-emerald-200 bg-emerald-50/50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isCompleted ? 'bg-green-100' : 'bg-emerald-100'
              }`}>
                {isCompleted ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                ) : (
                  <Calendar className="h-6 w-6 text-emerald-600" />
                )}
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-slate-800 text-lg">
                  {appointment.doctorName || 'Doctor Appointment'}
                </h2>
                {appointment.specialty && (
                  <p className="text-sm text-slate-600">{appointment.specialty}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${
                      isCompleted 
                        ? 'bg-green-100 text-green-700 border-green-200'
                        : appointment.status === 'ready'
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : 'bg-amber-100 text-amber-700 border-amber-200'
                    }`}
                  >
                    {isCompleted ? 'Completed' : appointment.status === 'ready' ? 'Ready' : 'Draft'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appointment Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-indigo-600" />
              Appointment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-slate-400" />
              <div>
                <div className="font-medium text-slate-800">
                  {appointment.appointmentDate && format(parseISO(appointment.appointmentDate), 'EEEE, MMMM d, yyyy')}
                </div>
                {appointment.appointmentTime && (
                  <div className="text-sm text-slate-500">at {appointment.appointmentTime}</div>
                )}
              </div>
            </div>
            {appointment.reason && (
              <div className="flex items-start gap-3">
                <ClipboardList className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-sm text-slate-500">Reason</div>
                  <div className="text-slate-800">{appointment.reason}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Symptoms */}
        {appointment.symptoms && appointment.symptoms.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Thermometer className="h-4 w-4 text-orange-600" />
                Symptoms & Concerns ({appointment.symptoms.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {appointment.symptoms.map((symptom, idx) => (
                  <li key={symptom.id || idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                    <div>
                      <span className="text-slate-800">{symptom.text}</span>
                      {symptom.startDate && (
                        <span className="text-xs text-slate-500 ml-2">
                          (started {format(parseISO(symptom.startDate), 'MMM d')})
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {appointment.changesSinceLastVisit && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs font-medium text-slate-600 mb-1">Changes since last visit:</div>
                  <p className="text-sm text-slate-700">{appointment.changesSinceLastVisit}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Questions */}
        {appointment.questions && appointment.questions.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-blue-600" />
                Questions ({appointment.questions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {appointment.questions.map((question, idx) => (
                  <li key={question.id || idx} className="flex items-start gap-2">
                    {question.answered ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-blue-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className={`text-slate-800 ${question.answered ? 'line-through text-slate-500' : ''}`}>
                        {question.text}
                      </span>
                      {question.answer && (
                        <div className="mt-1 p-2 bg-green-50 rounded text-sm text-green-700">
                          <span className="font-medium">Answer:</span> {question.answer}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Health Info Selections */}
        {(appointment.resolvedMedications?.length > 0 || 
          appointment.resolvedAllergies?.length > 0 || 
          appointment.resolvedConditions?.length > 0) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-purple-600" />
                Health Information Shared
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {appointment.resolvedMedications?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                    <Pill className="h-3.5 w-3.5 text-purple-500" />
                    Medications
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {appointment.resolvedMedications.map((med, idx) => (
                      <Badge key={med.id || idx} variant="secondary" className="text-xs">
                        {med.name} {med.dosage && `(${med.dosage})`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {appointment.resolvedAllergies?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                    Allergies
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {appointment.resolvedAllergies.map((allergy, idx) => (
                      <Badge key={allergy.id || idx} variant="outline" className="text-xs bg-red-50 text-red-700">
                        {allergy.name || allergy.allergen}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {appointment.resolvedConditions?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                    <Activity className="h-3.5 w-3.5 text-amber-500" />
                    Conditions
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {appointment.resolvedConditions.map((cond, idx) => (
                      <Badge key={cond.id || idx} variant="secondary" className="text-xs">
                        {cond.name || cond.condition}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Visit Notes (if completed) */}
        {isCompleted && appointment.visitNotes && (
          <Card className="border-green-200">
            <CardHeader className="pb-2 bg-green-50/50">
              <CardTitle className="text-base flex items-center gap-2 text-green-800">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Visit Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {appointment.visitNotes.doctorSaid && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-1">What the doctor said:</div>
                  <p className="text-slate-800">{appointment.visitNotes.doctorSaid}</p>
                </div>
              )}
              
              {appointment.visitNotes.diagnosis && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-1">Diagnosis:</div>
                  <p className="text-slate-800">{appointment.visitNotes.diagnosis}</p>
                </div>
              )}
              
              {appointment.visitNotes.instructions && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-1">Instructions:</div>
                  <p className="text-slate-800">{appointment.visitNotes.instructions}</p>
                </div>
              )}
              
              {appointment.visitNotes.medicationChanges && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-1">Medication changes:</div>
                  <p className="text-slate-800">{appointment.visitNotes.medicationChanges}</p>
                </div>
              )}
              
              {appointment.visitNotes.testsOrdered && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-1">Tests ordered:</div>
                  <p className="text-slate-800">{appointment.visitNotes.testsOrdered}</p>
                </div>
              )}
              
              {appointment.visitNotes.referrals && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-1">Referrals:</div>
                  <p className="text-slate-800">{appointment.visitNotes.referrals}</p>
                </div>
              )}
              
              {appointment.visitNotes.followUpNeeded && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <div className="text-sm font-medium text-amber-800 mb-1">Follow-up needed</div>
                  {appointment.visitNotes.followUpDate && (
                    <p className="text-sm text-amber-700">
                      Scheduled for {format(parseISO(appointment.visitNotes.followUpDate), 'MMMM d, yyyy')}
                    </p>
                  )}
                </div>
              )}
              
              {appointment.visitNotes.additionalNotes && (
                <div>
                  <div className="text-sm font-medium text-slate-600 mb-1">Additional notes:</div>
                  <p className="text-slate-800">{appointment.visitNotes.additionalNotes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 print:hidden">
          {!isCompleted ? (
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/medical/prepare?id=${appointment.id}`)}
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit Preparation
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate(`/medical/appointment/${appointment.id}/complete`)}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark Complete
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate(`/medical/appointment/${appointment.id}/complete`)}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit Visit Notes
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default AppointmentDetailPage;
